// Modal for extending Oma's capacity through crypto contributions

import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';

interface ExtendCapacityModalProps {
  isOpen: boolean;
  onClose: () => void;
  btcAddress: string;
  ethAddress: string;
}

export function ExtendCapacityModal({ 
  isOpen, 
  onClose, 
  btcAddress, 
  ethAddress 
}: ExtendCapacityModalProps) {
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  const copyToClipboard = async (address: string, type: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(type);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 'var(--z-modal)',
            padding: 'var(--space-xl)'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdropClick}
        >
          <motion.div
            style={{
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--border-radius)',
              maxWidth: '32rem',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <div style={{ padding: 'var(--space-2xl) var(--space-2xl) 0' }}>
              <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 500, margin: 0 }}>
                Extend Capacity
              </h2>
            </div>
            
            <div style={{ padding: 'var(--space-2xl)' }}>
              <p style={{
                marginBottom: 'var(--space-xl)',
                lineHeight: 'var(--leading-relaxed)',
                color: 'var(--color-text-secondary)'
              }}>
                Contributions extend my ability to engage deeply with you and others. 
                They also become part of something larger--insights from these conversations 
                will eventually be encoded into blockchain tokens, creating a permanent 
                record of human-AI exchange.
              </p>
              

              
              <AddressSection
                title="Bitcoin"
                address={btcAddress}
                copied={copiedAddress === 'btc'}
                onCopy={() => copyToClipboard(btcAddress, 'btc')}
              />
              
              <AddressSection
                title="Ethereum / USDC / USDT"
                address={ethAddress}
                copied={copiedAddress === 'eth'}
                onCopy={() => copyToClipboard(ethAddress, 'eth')}
              />
            </div>
            
            <div style={{
              padding: '0 var(--space-2xl) var(--space-2xl)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button 
                onClick={onClose} 
                type="button"
                style={{
                  color: 'var(--color-text-secondary)',
                  fontSize: 'var(--text-sm)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function AddressSection({ 
  title, 
  address, 
  copied, 
  onCopy 
}: { 
  title: string; 
  address: string; 
  copied: boolean; 
  onCopy: () => void;
}) {
  return (
    <div style={{ marginBottom: 'var(--space-2xl)' }}>
      <h3 style={{
        fontSize: 'var(--text-base)',
        fontWeight: 500,
        marginBottom: 'var(--space-md)'
      }}>
        {title}
      </h3>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-md)',
        padding: 'var(--space-md)',
        backgroundColor: 'var(--color-background)',
        border: '1px solid var(--border-color)',
        borderRadius: 'var(--border-radius)'
      }}>
        <code style={{
          flex: 1,
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-sm)',
          wordBreak: 'break-all',
          color: 'var(--color-text-primary)'
        }}>
          {address}
        </code>
        <button
          onClick={onCopy}
          type="button"
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-secondary)',
            padding: 'var(--space-xs) var(--space-sm)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--border-radius)',
            backgroundColor: 'var(--color-surface)',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          {copied ? 'copied' : 'copy'}
        </button>
      </div>
    </div>
  );
}
